/* eslint-disable max-lines */
/* eslint-disable react/prop-types */
/* eslint-disable max-lines-per-function */
import { useDispatch } from 'react-redux';
import { Blueprint, BlueprintInstanceType, BoundToEdge, Component, COMPONENT_PLACEMENT_FRONT_AND_TOP, COMPONENT_PLACEMENT_FRONT_ONLY, COMPONENT_PLACEMENT_TOP_ONLY, Components, EventCrudMessage, Panel, PanelItem, PanelItemData, PanelItemGroup, Point, ReferenceMap, Vertex2, Vertex2Data } from '@rta/rta-blueprint-library';
import { DisplayMode } from '@rta/rta-blueprint-component';
import { Box, Button, Center, Checkbox, FormLabel, Image, Select, Stack, Text, useColorModeValue } from '@chakra-ui/react';
import { useEffect, useRef, useState, type JSX } from 'react';

import { calculatePriceDelta } from './helpers';
import { ContextPanelContent } from './context-panel-content';
import { formatPrice, resolvePanelItemGroup } from '@helpers';
import { lang } from '@lang';
import { useDesign } from '@hooks';
import { InlineNumberInput, InlineSlider, StackedStringInput } from './controls';
import { popContextPanel, setContextPanelIsOpen } from '@state';
import { SkuSearchInput } from './sku-search-input';

/**
 * Triggers auto-distribution on a panel if SKU layer is active
 * @param panel The panel to distribute items on
 * @param controller The controller to check SKU layer status
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const triggerAutoDistributionIfSkuLayerActive = (panel: Panel | null, controller: any): void => {
	if (!panel) return;

	const currentDisplayMode = controller?.navigation?.displayMode;
	let shouldDistribute = false;

	switch (currentDisplayMode) {
		case DisplayMode.SkuOverhead:
		case DisplayMode.SkuOverheadManufacturing:
			shouldDistribute = true;
			break;
		default:
			shouldDistribute = false;
			break;
	}

	if (shouldDistribute) {
		// Use setTimeout to ensure the item is fully added before distributing
		setTimeout(() => {
			panel.distributeApplianceGroupsHorizontally();
		}, 0);
	}
};

type AddCustomAppliancePanelProps = {
	id: string | null;
	position?: Vertex2Data;
};

export const AddCustomAppliancePanel: React.FC<AddCustomAppliancePanelProps> = (props: AddCustomAppliancePanelProps): JSX.Element => {
	const { id, position = { x: 0, y: 0 }} = props;

	const colorSectionHeader = useColorModeValue('gray.600', 'gray.300');

	const dispatch = useDispatch();
	const [controller] = useDesign();

	const kitchen = controller?.kitchen ?? null;
	// const vendors = kitchen?.context.vendors;
	const categories = kitchen?.context.categories;
	const components = kitchen?.context.components ?? [];

	const [refMap, setRefMap] = useState<ReferenceMap | null>(null);
	// const [vendorId, setVendorId] = useState('');
	// const [categoryId, setCategoryId] = useState('');

	const [gasChoice, setGasChoice] = useState<string>('');
	const [panelItem, setPanelItem] = useState<PanelItem | null>(null);
	const [_piEvent, setPiEvent] = useState<EventCrudMessage | null>(null);
	const [cutsIntoCountertop, setCutsIntoCountertop] = useState<boolean>(false);
	const controllerSubs = useRef<string[]>([]);

	useEffect(() => {
		if (kitchen === null || id === null) return;

		// get the reference map for the provided id
		const map = kitchen?.getReferenceMap(id);
		if (map && map.id === id) setRefMap(map);

		if (map.type === BlueprintInstanceType.PanelItem) {
			setPanelItem(map.panelItem);
		} else {
			// Keep initial placement in sync with the initial category — otherwise picking Grills
			// (the default appliance category, defaultPlacement = FRONT_AND_TOP) and clicking Add
			// without touching the Placement dropdown would create a FRONT_ONLY item, triggering the
			// $300 CUSTOM_NONCOY_SIDEPANEL that the $1000 CUSTOM_NONCOY_DROPIN is meant to replace.
			const defaultCategory = categories?.find((cat) => cat.key === 'appliance');
			const initialPlacement = defaultCategory?.defaultPlacement ?? COMPONENT_PLACEMENT_FRONT_ONLY;
			const cutoutHeight = 12;
			const initialDistanceBelowCountertop =
				initialPlacement === COMPONENT_PLACEMENT_FRONT_AND_TOP || initialPlacement === COMPONENT_PLACEMENT_TOP_ONLY
					? cutoutHeight
					: 0;
			const initialDistanceFrontOfFace = initialPlacement === COMPONENT_PLACEMENT_FRONT_AND_TOP ? 0 : null;

			setPanelItem(new PanelItem({
				parent: map.kitchenDesign,
				dataIn: {
					item: {
						override: true,
						component: {
							description: 'Custom Appliance',
							sku: 'CUSTOM',
							categoryId: defaultCategory?.id ?? '',
							placement: initialPlacement,
							cutout: { width: 12, height: cutoutHeight, depth: 1 },
							size: { width: 13, height: 13, depth: 1 },
							msrp: 0,
							distanceBelowCountertop: initialDistanceBelowCountertop,
							distanceFrontOfFace: initialDistanceFrontOfFace
						}
					}
				}
			}));
		}
	}, [kitchen, id]);

	useEffect(() => {
		if (panelItem) {
			panelItem.item.component.readonly = false;
			const sub = panelItem.subscribe('*', '*', setPiEvent);
			if (sub) controllerSubs.current.push(sub);
		}

		// cleanup subscriptions
		return () => controllerSubs.current.forEach((sub: string) => controller?.unsubscribe(sub));
	}, [panelItem]);

	// update the panel item when the event is updated
	// useEffect(() => {
	// 	if (piEvent) console.log('AddCustomAppliancePanel:useEffect:piEvent', piEvent);
	// }, [piEvent]);

	if (!kitchen || !refMap || !refMap.panel || !panelItem) return <></>;

	const applyPlacement = (placement: string): void => {
		if (!panelItem.item.component) return;
		panelItem.item.component.placement = placement;

		const { cutout } = panelItem.item.component;

		if (placement === Components.PLACEMENT_FRONT_AND_TOP) {
			panelItem.item.component.distanceBelowCountertop = cutout.height;
		} else if (placement === Components.PLACEMENT_TOP_ONLY) {
			panelItem.item.component.distanceBelowCountertop = cutout.height;
			panelItem.item.component.distanceFrontOfFace = null;
			panelItem.center.z = 0;
		} else {
			panelItem.item.component.distanceBelowCountertop = 0;
		}

		// Sync vertical edge binding to the new placement (matches PanelItem constructor / withPlaceholderItem).
		// Without this, a FRONT_ONLY item switched from a Top-bound default keeps boundTop=true and lands
		// stuck to the top edge of the panel like a drop-in front cutout.
		const verticalBound = placement === Components.PLACEMENT_FRONT_AND_TOP || placement === Components.PLACEMENT_TOP_ONLY
			? BoundToEdge.Top
			: placement === Components.PLACEMENT_BOTTOM_ONLY
				? BoundToEdge.Bottom
				: BoundToEdge.No;
		// eslint-disable-next-line no-bitwise
		panelItem.boundToEdge = (panelItem.boundToEdge & (BoundToEdge.Left | BoundToEdge.Right)) | verticalBound;

		setCutsIntoCountertop(placement === Components.PLACEMENT_TOP_ONLY);

		if (placement !== Components.PLACEMENT_TOP_ONLY && panelItem.item.component.distanceFrontOfFace === null) {
			panelItem.item.component.distanceFrontOfFace =
				placement === Components.PLACEMENT_FRONT_AND_TOP
					? 0
					: null;
		}
	};

	// console.log('render:AddCustomAppliancePanelItem', id, position, refMap.panel, panelItem);
	const title = lang.contextPanels.title.addCustomAppliance;

	console.log('panelItem', panelItem);

	return <ContextPanelContent title={title}>
		<Stack width="100%" pt="10px" spacing={'5px'}>
			<StackedStringInput
				label="Sku"
				placeholder="Sku code..."
				value={panelItem.item.component?.customLabel ?? panelItem.item.component?.label}
				onChange={((e) => {
					if (panelItem.item.component) panelItem.item.component.customLabel = e.target.value;
				})}
			/>
			<StackedStringInput
				label="Description"
				placeholder="Appliance description..."
				value={panelItem.item.component?.description ?? ''}
				onChange={((e) => {
					if (panelItem.item.component) panelItem.item.component.description = e.target.value;
				})}
			/>

			<Box>
				<FormLabel htmlFor="vendor" fontSize="small" marginBottom="0">Vendor:</FormLabel>
				<Select id="vendor" height="2em"
					fontSize="small"
					value={panelItem.item.component?.vendorId ?? ''}
					onChange={(e) => {
						if (panelItem.item.component) {
							panelItem.item.component.vendorId = e.target.value;
						}
					}}
				>
					<option value="">Other</option>
					{ kitchen.context.vendors
						.filter((v) => v.active)
						.map((v, i) => <option key={i} value={v.id}>{v.displayValue}</option>)}
				</Select>
			</Box>

			<Box>
				<FormLabel htmlFor="appliance-type" fontSize="small" marginBottom="0">Type:</FormLabel>
				<Select id="appliance-type" height="2em"
					fontSize="small"
					value={panelItem.item.component?.categoryId ?? ''}
					onChange={(e) => {
						if (panelItem.item.component) panelItem.item.component.categoryId = e.target.value;
						const { defaultPlacement } = kitchen.context.categories.findById(e.target.value) ?? {};
						if (defaultPlacement) applyPlacement(defaultPlacement);
					}}
				>
					{ kitchen.context.categories
						.filter((c) => c.active)
						.map((cat, i) => <option key={i} value={cat.id}>{cat.description}</option>)}
				</Select>
			</Box>

			<Box>
				<FormLabel htmlFor="appliance-placement" fontSize="small" marginBottom="0">Placement:</FormLabel>
				<Select id="appliance-placement" height="2em"
					fontSize="small"
					value={panelItem.item.component?.placement ?? ''}
					onChange={(e) => applyPlacement(e.target.value)}
				>
					{ Components.placement.map((p, i) => {
						return <option key={i} value={p.key}>{p.displayName}</option>;
					})}
				</Select>
			</Box>

			{ panelItem.item.component?.placement === Components.PLACEMENT_TOP_ONLY &&
				<Checkbox
					isChecked={cutsIntoCountertop}
					onChange={(e) => {
						const { checked } = e.target;
						setCutsIntoCountertop(checked);
						if (panelItem.item.component) {
							panelItem.item.component.distanceBelowCountertop = checked
								? panelItem.item.component.cutout.height
								: 0;
						}
					}}
				>
					<Text fontSize="small">Cuts Into Countertop</Text>
				</Checkbox>
			}

			<Box>
				<FormLabel htmlFor="appliance-gas-choice" fontSize="small" marginBottom="0">Fuel Options:</FormLabel>
				<Select id="appliance-gas-choice" height="2em"
					fontSize="small"
					value={gasChoice}
					onChange={(event) => setGasChoice(event.target.value)}
				>
					<option value="">Use Kitchen Default</option>
					<option value="CH">Charcoal</option>
					<option value="EL">Electric</option>
					<option value="PE">Pellet</option>
					<option value="LP">Propane</option>
					<option value="NG">Natural Gas</option>
				</Select>
			</Box>
			<Box>
				<InlineNumberInput
					category="MSRP"
					label="MSRP"
					min={0}
					value={panelItem.item.msrp}
					onChange={(e) => { panelItem.item.msrp = e as number; }}
				/>
			</Box>
			<Box marginBottom={3} padding={2} textAlign="left" borderLeft="1px solid grey">
				<Text paddingBottom={3} fontSize="medium">Dimensional Info.</Text>
				<Stack spacing="3" width="100%" height="65%">
					<InlineNumberInput
						category="cutout"
						label="Cutout Width"
						min={0.5}
						value={panelItem.item.cutout.width}
						onChange={(e) => { panelItem.item.cutout = { width: e }; }}
					/>
					<InlineNumberInput
						category="cutout"
						label="Cutout Height"
						min={0.5}
						value={panelItem.item.cutout.height}
						onChange={(e) => {
							panelItem.item.cutout = { height: e };
							const { placement, distanceBelowCountertop } = panelItem.item.component;
							const cutsIntoCountertop = placement === Components.PLACEMENT_TOP_ONLY && distanceBelowCountertop > 0;
							if (placement === Components.PLACEMENT_FRONT_AND_TOP || cutsIntoCountertop) {
								panelItem.item.component.distanceBelowCountertop = e;
							}
						}}
					/>
					<InlineNumberInput
						category="cutout"
						label="Cutout Depth"
						min={0}
						value={panelItem.item.cutout.depth}
						onChange={(e) => { panelItem.item.cutout = { depth: e }; }}
					/>
					<InlineNumberInput
						category="appliance" label="Overall Width" min={0.5}
						value={panelItem.item.size.width}
						onChange={(e) => { panelItem.item.size = { width: e }; }}
					/>
					<InlineNumberInput
						category="appliance" label="Overall Height" min={0.5}
						value={panelItem.item.size.height}
						onChange={(e) => { panelItem.item.size = { height: e }; }}
					/>
					<InlineNumberInput
						category="appliance" label="Overall Depth" min={0}
						value={panelItem.item.component.size.depth}
						onChange={(e) => { panelItem.item.size = { depth: e }; }}
					/>
					<InlineNumberInput
						category="appliance" label="Distance Below Counter" min={0}
						value={ panelItem.item.component.distanceBelowCountertop ?? 0 }
						enabled={
							panelItem.item.component.placement === Components.PLACEMENT_FRONT_AND_TOP
							|| cutsIntoCountertop
						}
						onChange={(e) => { panelItem.item.component.distanceBelowCountertop = e; }}
					/>
					<InlineNumberInput
						category="appliance" label="Distance Front Of Face" min={0}
						enabled={panelItem.item.component.placement === Components.PLACEMENT_FRONT_AND_TOP}
						value={ panelItem.item.component.distanceFrontOfFace }
						onChange={(e) => {
							panelItem.item.component.distanceFrontOfFace = panelItem.item.component.placement === Components.PLACEMENT_FRONT_AND_TOP
								? e
								: null;
						}}
					/>
				</Stack>
			</Box>

			{ refMap.panelItem &&
				<Box>
					<Text pt="5px" pl="5px" color={colorSectionHeader} fontSize="medium"
						fontWeight="medium"
					>Dimensional Details</Text>
					<Box mb={3} padding={2} textAlign="left"
						borderLeft="1px solid grey" borderTop="1px" roundedTopLeft="7px"
					>
						<Stack spacing="3" width="100%" height="65%">
							<InlineSlider
								category="dimension" label="H. Line"
								min={0} max={1} step={0.01}
								value={ panelItem.dimensionOffsets.hLine }
								onChange={(e) => {
									panelItem.dimensionOffsets.hLine = e;
								}}
							/>
							<InlineSlider
								category="dimension" label="H. Label"
								min={0} max={1} step={0.01}
								value={ panelItem.dimensionOffsets.hLabel }
								onChange={(e) => {
									panelItem.dimensionOffsets.hLabel = e;
								}}
							/>
							<InlineSlider
								category="dimension" label="V. Line"
								min={0} max={1} step={0.01}
								value={ panelItem.dimensionOffsets.vLine }
								onChange={(e) => {
									panelItem.dimensionOffsets.vLine = e;
								}}
							/>
							<InlineSlider
								category="dimension" label="V. Label"
								min={0} max={1} step={0.01}
								value={ panelItem.dimensionOffsets.vLabel }
								onChange={(e) => {
									panelItem.dimensionOffsets.vLabel = e;
								}}
							/>
						</Stack>
					</Box>
				</Box>
			}

			<Box>
				<SkuSearchInput
					components={[...components]}
					value={panelItem.item.component.placeholderModelSku}
					excludeSku={panelItem.item.component.sku}
					onChange={(sku) => { panelItem.item.component.placeholderModelSku = sku; }}
				/>
			</Box>

			<Box pt="10px" width="100%">
				<Button width="100%"
					onClick={() => {
						dispatch(popContextPanel());

						// eslint-disable-next-line no-bitwise
						panelItem.item.component.distanceAboveCountertop = (panelItem.boundToEdge & BoundToEdge.Top) > 0
							? Math.max(0, panelItem.item.size.height - panelItem.item.distanceBelowCountertop)
							: null;

						panelItem.center.y = position.y;
						panelItem.item.component.sku = 'CUSTOM';

						if (!refMap.panelItem) {
							const targetGroup = resolvePanelItemGroup({
								refMap,
								placement: panelItem.item.component.placement,
								fallbackX: position.x
							});

							if (targetGroup) {
								targetGroup.items.new(panelItem.serialize());
								// Auto-distribute if SKU layer is active
								triggerAutoDistributionIfSkuLayerActive(refMap.panel, controller);
							}
						}
					}}
				>{refMap.panelItem ? 'Close' : 'Add'}</Button>
			</Box>
		</Stack>
	</ContextPanelContent>;
};
